import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { apiErrorCode, crmErrorDetail, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS, STAGES, STAGE_KEYS } from "../contract";
import { monthNames } from "../format";
import useTags from "../hooks/useTags";
import { clientHref } from "../paths";
import SlideOver from "../ui/SlideOver";
import CrmInput from "../ui/CrmInput";
import CrmSelect from "../ui/CrmSelect";
import CrmButton from "../ui/CrmButton";
import TagInput from "../ui/TagInput";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const SCHEMA = z.object({
  name: z.string().trim().min(1, "add.errNameRequired").max(200, "add.errTooLong"),
  email: z.string().trim().min(1, "add.errEmailRequired").email("add.errEmailInvalid"),
  phone: z.string().trim().max(40, "add.errTooLong"),
});

const EMPTY = { name: "", email: "", phone: "", day: "", month: "", tags: [], stage: "lead" };
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * "+ Add client" slide-over (spec Screen 2): name (required), email (required,
 * validated), phone, birthday (day + month is enough), tags, stage (default Lead).
 *
 * A duplicate email shows "Client exists — open record" linking to the existing record
 * INSTEAD of creating a second one: checked as soon as the email field is left
 * (`GET /api/crm/clients/lookup`), and enforced by the server regardless (409
 * CLIENT_EXISTS carries the id). While it shows, Save is disabled.
 *
 * There is no consent field on purpose (decision 12): a manual client is Not consented
 * until they tick a capture form themselves.
 */
export default function AddClientSheet({ open, onOpenChange, onCreated }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { tags: known } = useTags();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setForm(EMPTY); setErrors({}); setExisting(null); } }, [open]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    if (key === "email") setExisting(null);
  };

  const checkDuplicate = async () => {
    const email = form.email.trim();
    if (!z.string().email().safeParse(email).success) return;
    try {
      const hit = await crmApi.lookupClientByEmail(email);
      setExisting(hit?.client_id ? hit.client_id : null);
    } catch (e) {
      // CLIENT_NOT_FOUND is the "no duplicate" answer; any other failure leaves the check
      // to the server's 409 on Save rather than blocking the form.
      setExisting(null);
    }
  };

  const save = async () => {
    const parsed = SCHEMA.safeParse(form);
    if (!parsed.success) {
      const next = {};
      parsed.error.issues.forEach((i) => { next[i.path[0]] = i.message; });
      setErrors(next);
      return;
    }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), stage: form.stage };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.day && form.month) payload.birthday = { day: Number(form.day), month: Number(form.month) };
      if (form.tags.length) payload.tags = form.tags;
      const created = await crmApi.createClient(payload);
      toast.success(t("add.created", { name: payload.name }));
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === CRM_ERRORS.clientExists) setExisting(crmErrorDetail(e).client_id || null);
      else if (code === CRM_ERRORS.emailInvalid) setErrors((x) => ({ ...x, email: "add.errEmailInvalid" }));
      else toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const months = monthNames(lang);
  const err = (key) => errors[key] && <p role="alert" className="mt-1 text-xs text-red-700">{t(errors[key])}</p>;

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title={t("add.title")}
      testId={CRM.addClient}
      footer={(
        <CrmButton size="full" onClick={save} disabled={busy || !!existing} data-testid={CRM.addClientSave}>
          {t("common.save")}
        </CrmButton>
      )}
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
        <div>
          <label htmlFor="crm-add-name" className={LABEL}>{t("add.name")} *</label>
          <CrmInput id="crm-add-name" value={form.name} onChange={(e) => set("name", e.target.value)} invalid={!!errors.name}
            autoComplete="off" data-testid={CRM.addClientField("name")} />
          {err("name")}
        </div>
        <div>
          <label htmlFor="crm-add-email" className={LABEL}>{t("add.email")} *</label>
          <CrmInput id="crm-add-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={checkDuplicate}
            invalid={!!errors.email} autoComplete="off" data-testid={CRM.addClientField("email")} />
          {err("email")}
        </div>
        {existing && (
          <p data-testid={CRM.addClientExists} className="rounded-2xl bg-eh-cream-deep px-4 py-3 text-sm text-eh-ink">
            {t("add.exists")}{" "}
            <Link to={clientHref(existing)} onClick={() => onOpenChange(false)} className="underline" data-testid={CRM.addClientOpenRecord}>
              {t("add.openRecord")}
            </Link>
          </p>
        )}
        <div>
          <label htmlFor="crm-add-phone" className={LABEL}>{t("add.phone")}</label>
          <CrmInput id="crm-add-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} invalid={!!errors.phone}
            data-testid={CRM.addClientField("phone")} />
          {err("phone")}
        </div>
        <fieldset>
          <legend className={LABEL}>{t("add.birthday")}</legend>
          <div className="flex gap-2">
            <CrmSelect aria-label={t("add.birthdayDay")} value={form.day} onChange={(e) => set("day", e.target.value)}
              placeholder={t("add.day")} options={DAYS.map((d) => ({ value: String(d), label: String(d) }))}
              data-testid={CRM.addClientField("birthday-day")} />
            <CrmSelect aria-label={t("add.birthdayMonth")} value={form.month} onChange={(e) => set("month", e.target.value)}
              placeholder={t("add.month")} options={months.map((m, i) => ({ value: String(i + 1), label: m }))}
              data-testid={CRM.addClientField("birthday-month")} />
          </div>
        </fieldset>
        <div>
          <span className={LABEL} id="crm-add-tags-label">{t("add.tags")}</span>
          <TagInput value={form.tags} onChange={(v) => set("tags", v)} suggestions={known} label={t("add.tags")}
            placeholder={t("add.tagsPlaceholder")} testId={CRM.addClientField("tags")} />
        </div>
        <div>
          <label htmlFor="crm-add-stage" className={LABEL}>{t("add.stage")}</label>
          <CrmSelect id="crm-add-stage" value={form.stage} onChange={(e) => set("stage", e.target.value)}
            options={STAGES.map((s) => ({ value: s, label: t(STAGE_KEYS[s]) }))} data-testid={CRM.addClientField("stage")} />
        </div>
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
      </form>
    </SlideOver>
  );
}
